import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "./ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { toast } from "sonner";
import { 
  ArrowLeft, 
  Mail, 
  Lock, 
  Eye, 
  EyeOff, 
  Building2, 
  LogIn,
  AlertCircle
} from "lucide-react";
import { agentAdvancerSenzaSfondo } from '../images';
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';

const LoginPage = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    email: "",
    password: ""
  });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: "" }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.email) {
      newErrors.email = "Email richiesta";
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = "Email non valida";
    }
    
    if (!formData.password) {
      newErrors.password = "Password richiesta";
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Pulizia localStorage prima di ogni login
    localStorage.removeItem('token');
    localStorage.removeItem('agenzia_token');
    localStorage.removeItem('user_type');
    localStorage.removeItem('candidato_id');
    localStorage.removeItem('agenzia_id');
    localStorage.removeItem('admin_token');

    if (!validateForm()) {
      return;
    }

    setLoading(true);

    try {
      // Login unificato: backend prova candidato → agenzia → admin
      const response = await axios.post(`${API}/api/v1/auth/login`, {
        email: formData.email,
        password: formData.password
      });
      if (response.data && response.data.access_token) {
        const userType = response.data.user_type;
        const userId = response.data.user_id;
        const redirect = response.data.redirect_url;
        
        // Pulisci vecchi token prima di salvare i nuovi
        localStorage.removeItem('token');
        localStorage.removeItem('impersonated_token');
        localStorage.removeItem('candidato_id');
        localStorage.removeItem('agenzia_id');
        localStorage.removeItem('admin_token');
        
        // Salva i nuovi token
        localStorage.setItem('token', response.data.access_token);
        localStorage.setItem('user_type', userType);
        if (userType === 'candidato') {
          localStorage.setItem('candidato_id', userId);
        } else if (userType === 'agenzia') {
          localStorage.setItem('agenzia_id', userId);
        } else if (userType === 'admin') {
          localStorage.setItem('admin_token', response.data.access_token);
        }
        
        toast.success('Login effettuato con successo');
        
        // Attendi un attimo che localStorage sia aggiornato, poi naviga
        setTimeout(() => {
          // Se usa HashRouter, aggiungi # al redirect
          const finalRedirect = redirect ? `/#${redirect}` : '/';
          window.location.href = finalRedirect;
        }, 100);
        return;
      }
    } catch (err) {
      toast.error('Credenziali non valide');
      setErrors({ email: 'Credenziali non valide', password: 'Credenziali non valide' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 pb-28 md:pb-8">
      <div className="container mx-auto px-4 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <Button
            variant="ghost"
            onClick={() => navigate('/accesso')}
            className="rounded-xl"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Indietro
          </Button>
          
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 bg-white rounded-lg flex items-center justify-center p-1">
              <img 
                src={agentAdvancerSenzaSfondo} 
                alt="InLeads Logo" 
                className="w-full h-full object-contain"
              />
            </div>
            <span className="font-semibold text-gray-900">InLeads</span>
          </div>
        </div>

        {/* Main Login Card */}
        <div className="max-w-md mx-auto">
          <Card className="border-0 shadow-2xl rounded-3xl overflow-hidden">
            <CardHeader className="bg-gradient-to-r from-blue-500 to-purple-600 text-white p-8 text-center">
              <div className="w-16 h-16 bg-white/20 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <LogIn className="w-8 h-8 text-white" />
              </div>
              <CardTitle className="text-2xl font-bold">
                Bentornato
              </CardTitle>
              <p className="text-blue-100 mt-2">
                Accedi al tuo account InLeads
              </p>
            </CardHeader>

            <CardContent className="p-8">
              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Email Field */}
                <div>
                  <Label htmlFor="email" className="text-sm font-medium text-gray-700 mb-2 block">
                    Email *
                  </Label>
                  <div className="relative">
                    <Mail className="w-5 h-5 text-gray-400 absolute left-3 top-1/2 transform -translate-y-1/2" />
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleChange}
                      placeholder="la-tua-email@esempio.com"
                      className={`pl-10 rounded-xl border-gray-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-100 ${
                        errors.email ? 'border-red-300 focus:border-red-500 focus:ring-red-100' : ''
                      }`}
                    />
                  </div>
                  {errors.email && (
                    <div className="flex items-center gap-2 mt-2 text-red-600 text-sm">
                      <AlertCircle className="w-4 h-4" />
                      {errors.email}
                    </div>
                  )}
                </div>

                {/* Password Field */}
                <div>
                  <Label htmlFor="password" className="text-sm font-medium text-gray-700 mb-2 block">
                    Password *
                  </Label>
                  <div className="relative">
                    <Lock className="w-5 h-5 text-gray-400 absolute left-3 top-1/2 transform -translate-y-1/2" />
                    <Input
                      id="password"
                      name="password"
                      type={showPassword ? "text" : "password"}
                      value={formData.password}
                      onChange={handleChange}
                      placeholder="La tua password"
                      className={`pl-10 pr-10 rounded-xl border-gray-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-100 ${
                        errors.password ? 'border-red-300 focus:border-red-500 focus:ring-red-100' : ''
                      }`}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    >
                      {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                    </button>
                  </div>
                  {errors.password && (
                    <div className="flex items-center gap-2 mt-2 text-red-600 text-sm">
                      <AlertCircle className="w-4 h-4" />
                      {errors.password}
                    </div>
                  )}
                </div>

                {/* Forgot Password Link */}
                <div className="text-right">
                  <button
                    type="button"
                    onClick={() => navigate('/forgot-password')}
                    className="text-sm text-blue-600 hover:text-blue-700 font-medium hover:underline"
                  >
                    Password dimenticata?
                  </button>
                </div>

                {/* Submit Button */}
                <Button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700 text-white rounded-xl py-3 text-lg font-semibold shadow-lg hover:shadow-xl transition-all duration-300"
                >
                  {loading ? (
                    <>
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                      Accesso...
                    </>
                  ) : (
                    <>
                      <LogIn className="w-5 h-5 mr-2" />
                      Accedi
                    </>
                  )}
                </Button>

                {/* Account Demo section removed */}

                {/* Register Link */}
                <div className="text-center pt-4 border-t border-gray-100">
                  <p className="text-gray-600 text-sm">
                    Non hai un account?{" "}
                    <button
                      type="button"
                      onClick={() => navigate('/registrati')}
                      className="text-blue-600 hover:text-blue-700 font-semibold"
                    >
                      Registrati qui
                    </button>
                  </p>
                </div>
              </form>
            </CardContent>
          </Card>
        </div>

        {/* Footer Trust Indicators */}
        <div className="mt-12 text-center">
          <div className="flex justify-center items-center gap-6 flex-wrap text-gray-500 text-sm">
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 bg-green-500 rounded-full"></div>
              <span>Connessione sicura</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
              <span>Dati protetti</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 bg-purple-500 rounded-full"></div>
              <span>GDPR compliant</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;